import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '@/api/client';
import PageHeader from '@/components/PageHeader';
import { ErrorBanner, ToggleField } from '@/components/ui';
import { useToast } from '@/components/Toast';
import { useMe } from '@/context/MeContext';
import type { MeQuotas } from '@/api/services/me';
import type { Source } from '@/types';
import { resolveCapabilities, searchPlanValidationLimits } from '@/utils/capabilities';
import {
  duplicateSubscriptionId,
  formatApiError,
  isDuplicateSubscriptionError,
} from '@/utils/formatApiError';
import CategoryCards from './CategoryCards';
import CustomWizard from './categories/CustomWizard';
import JobWizard from './categories/JobWizard';
import MarketplaceWizard from './categories/MarketplaceWizard';
import RealEstateWizard from './categories/RealEstateWizard';
import VehicleWizard from './categories/VehicleWizard';
import WizardCreatingPanel from './WizardCreatingPanel';
import WizardFooter from './WizardFooter';
import WizardProgress from './WizardProgress';
import WizardSuccessPanel from './WizardSuccessPanel';
import WizardSummary from './WizardSummary';
import {
  buildFilters,
  categoryForApi,
  createEmptyForm,
  type QuickSetupFormState,
  type SearchCategory,
} from './types';
import {
  canSubmitWizard,
  CREATION_PROGRESS_STEPS,
  deriveStepperState,
  initialWizardPhase,
  isWizardLocked,
  nextPhase,
  previousPhase,
  validateBeforeAdvance,
  validateBeforeCreate,
  type WizardPhase,
} from './wizardState';

const wizardByCategory = {
  vehicles: VehicleWizard,
  real_estate: RealEstateWizard,
  jobs: JobWizard,
  marketplace: MarketplaceWizard,
  custom: CustomWizard,
} as const;

export default function QuickSetup() {
  const { toast } = useToast();
  const { me } = useMe();
  const readOnly = me?.permissions.readOnly ?? false;
  const canManage = me?.permissions.canManageSources ?? false;

  const [phase, setPhase] = useState<WizardPhase>(initialWizardPhase);
  const [form, setForm] = useState<QuickSetupFormState>(createEmptyForm());
  const [sources, setSources] = useState<Source[]>([]);
  const [createdSearchId, setCreatedSearchId] = useState<string | null>(null);
  const [createdSubscriptionId, setCreatedSubscriptionId] = useState<string | null>(null);
  const [duplicateSubId, setDuplicateSubId] = useState<string | null>(null);
  const [jobsQueued, setJobsQueued] = useState(0);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [creationProgressIndex, setCreationProgressIndex] = useState(0);
  const [createError, setCreateError] = useState<string | null>(null);
  const [quotas, setQuotas] = useState<MeQuotas | null>(null);
  const submitInFlight = useRef(false);

  const capabilities = useMemo(() => resolveCapabilities(me, quotas), [me, quotas]);
  const planLimits = useMemo(
    () => searchPlanValidationLimits(capabilities, quotas?.usage?.searches),
    [capabilities, quotas?.usage?.searches],
  );

  const stepper = useMemo(() => deriveStepperState(phase), [phase]);
  const formLocked = isWizardLocked(phase);

  const resetWizard = useCallback(
    (category?: SearchCategory) => {
      setForm({
        ...createEmptyForm(category ?? form.category),
        source_names: sources.length ? sources.map((x) => x.name) : [],
      });
      setPhase(initialWizardPhase());
      setCreatedSearchId(null);
      setCreatedSubscriptionId(null);
      setDuplicateSubId(null);
      setJobsQueued(0);
      setActiveJobId(null);
      setCreationProgressIndex(0);
      setCreateError(null);
      submitInFlight.current = false;
    },
    [form.category, sources],
  );

  useEffect(() => {
    api.sources
      .list()
      .then((s) => {
        const enabled = s.filter((x) => x.is_enabled);
        setSources(enabled);
        setForm((prev) => ({
          ...prev,
          source_names: enabled.length ? enabled.map((x) => x.name) : prev.source_names,
        }));
      })
      .catch((e) => toast(e.message, 'error'));
  }, [toast]);

  useEffect(() => {
    if (!me?.id) return;
    api.me
      .quotas()
      .then(setQuotas)
      .catch(() => setQuotas(null));
  }, [me?.id]);

  useEffect(() => {
    if (phase !== 'creating') {
      setCreationProgressIndex(0);
      return undefined;
    }

    setCreationProgressIndex(0);
    const timers = CREATION_PROGRESS_STEPS.map((_, index) =>
      window.setTimeout(() => setCreationProgressIndex(index), index * 450),
    );
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [phase]);

  function patchForm(patch: Partial<QuickSetupFormState>) {
    if (formLocked) return;
    setForm((prev) => ({ ...prev, ...patch }));
  }

  function selectCategory(category: SearchCategory) {
    if (formLocked) return;
    setForm({
      ...createEmptyForm(category),
      source_names: sources.length ? sources.map((x) => x.name) : [],
    });
  }

  function toggleSource(name: string) {
    if (formLocked) return;
    setForm((prev) => {
      const selected = prev.source_names.includes(name)
        ? prev.source_names.filter((n) => n !== name)
        : [...prev.source_names, name];
      if (
        capabilities.enforcePlanLimits &&
        planLimits.maxSourcesPerSearch != null &&
        !prev.source_names.includes(name) &&
        selected.length > planLimits.maxSourcesPerSearch
      ) {
        toast(
          `Your plan allows up to ${planLimits.maxSourcesPerSearch} sources per search.`,
          'error',
        );
        return prev;
      }
      return { ...prev, source_names: selected };
    });
  }

  function setSourceNames(source_names: string[]) {
    if (formLocked) return;
    if (
      capabilities.enforcePlanLimits &&
      planLimits.maxSourcesPerSearch != null &&
      source_names.length > planLimits.maxSourcesPerSearch
    ) {
      setForm((prev) => ({
        ...prev,
        source_names: source_names.slice(0, planLimits.maxSourcesPerSearch),
      }));
      return;
    }
    setForm((prev) => ({ ...prev, source_names }));
  }

  function goNext() {
    if (formLocked) return;
    const err = validateBeforeAdvance(phase, form, planLimits);
    if (err) {
      toast(err, 'error');
      return;
    }
    const next = nextPhase(phase);
    if (next) setPhase(next);
  }

  function goBack() {
    if (formLocked) return;
    const prev = previousPhase(phase);
    if (prev) {
      setCreateError(null);
      setPhase(prev);
    }
  }

  async function createAlert() {
    if (submitInFlight.current) return;
    if (phase !== 'review' && phase !== 'error') return;

    const err = validateBeforeCreate(form, planLimits);
    if (err) {
      toast(err, 'error');
      return;
    }

    submitInFlight.current = true;
    setCreateError(null);
    setPhase('creating');

    try {
      const res = await api.wizard.setup({
        telegram_chat_id: form.telegram_chat_id,
        name: form.name,
        category: categoryForApi(form.category),
        source_names: form.source_names,
        filters_json: buildFilters(form),
        run_scrape: form.run_scrape,
      });
      setCreationProgressIndex(CREATION_PROGRESS_STEPS.length);
      setCreatedSearchId(res.search_id);
      setCreatedSubscriptionId(res.subscription_id);
      setJobsQueued(res.jobs_queued);
      if (res.job_id) setActiveJobId(res.job_id);
      setPhase('success');
      if (res.warnings.length > 0) {
        res.warnings.forEach((w) => toast(w, 'info'));
      }
    } catch (err) {
      if (isDuplicateSubscriptionError(err)) {
        setDuplicateSubId(duplicateSubscriptionId(err));
        setCreateError('This alert already exists for this recipient and search.');
        setPhase('duplicate');
      } else {
        setCreateError(formatApiError(err, 'Setup failed'));
        setPhase('error');
      }
      submitInFlight.current = false;
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmitWizard(phase)) return;
    await createAlert();
  }

  function handleFormKeyDown(e: React.KeyboardEvent<HTMLFormElement>) {
    if (phase === 'creating' && e.key === 'Enter') {
      e.preventDefault();
    }
  }

  const CategoryWizard = wizardByCategory[form.category];
  const sourcesEmptyHint =
    sources.length === 0 && !canManage
      ? 'No sources assigned. Ask your workspace admin to assign sources before creating searches.'
      : sources.length === 0 && canManage
        ? 'No sources enabled for this workspace. Enable sources in Workspace settings.'
        : undefined;

  return (
    <div className="quick-setup-page">
      <PageHeader
        title="Quick setup"
        subtitle="Category-driven wizard — configure a search, recipient and alert in a few steps"
      />

      {readOnly && (
        <p className="muted section">Your role is read-only. You cannot create new alerts.</p>
      )}

      {!readOnly && (
        <div
          className={`card section card-elevated quick-setup-wizard${
            formLocked ? ' quick-setup-wizard--locked' : ''
          }`}
        >
          <WizardProgress {...stepper} />

          <form className="wizard-body" onSubmit={submit} onKeyDown={handleFormKeyDown}>
            <fieldset className="wizard-step-panel" disabled={formLocked}>
              {phase === 'category' && (
                <>
                  <header className="wizard-step-header">
                    <h2 className="wizard-step-title">Choose a category</h2>
                    <p className="muted wizard-step-intro">
                      Pick what you want to monitor. The form adapts to your selection.
                    </p>
                  </header>
                  <CategoryCards value={form.category} onChange={selectCategory} />
                </>
              )}

              {phase === 'search' && (
                <>
                  <header className="wizard-step-header">
                    <h2 className="wizard-step-title">Configure search</h2>
                  </header>
                  <CategoryWizard
                    form={form}
                    sources={sources}
                    onChange={patchForm}
                    onToggleSource={toggleSource}
                    onSetSourceNames={setSourceNames}
                    sourcesEmptyHint={sourcesEmptyHint}
                  />
                </>
              )}

              {phase === 'recipient' && (
                <>
                  <header className="wizard-step-header">
                    <h2 className="wizard-step-title">Recipient</h2>
                    <p className="muted wizard-step-intro">
                      Who should receive alerts for this search?
                    </p>
                  </header>
                  <div className="wizard-field-grid wizard-field-grid--recipient">
                    <label>
                      Telegram recipient
                      <input
                        value={form.telegram_chat_id}
                        onChange={(e) => patchForm({ telegram_chat_id: e.target.value })}
                        placeholder="me or @username"
                        required
                      />
                    </label>
                    <label>
                      Alert name
                      <input
                        value={form.name}
                        onChange={(e) => patchForm({ name: e.target.value })}
                        placeholder="my-alert-name"
                        required
                      />
                    </label>
                  </div>
                </>
              )}

              {phase === 'review' && (
                <>
                  <header className="wizard-step-header">
                    <h2 className="wizard-step-title">Review and create</h2>
                  </header>
                  <WizardSummary form={form} />
                  <ToggleField
                    checked={form.run_scrape}
                    onChange={(next) => patchForm({ run_scrape: next })}
                    label="Run scrape now (test immediately)"
                    className="wizard-run-scrape"
                  />
                </>
              )}

              {phase === 'creating' && (
                <WizardCreatingPanel activeIndex={creationProgressIndex} />
              )}

              {phase === 'success' && (
                <WizardSuccessPanel
                  jobsQueued={jobsQueued}
                  activeJobId={activeJobId}
                  createdSubscriptionId={createdSubscriptionId}
                  onSelectJob={(id) => setActiveJobId(id)}
                />
              )}

              {phase === 'duplicate' && (
                <div className="wizard-error-panel" role="alert">
                  <header className="wizard-step-header">
                    <h2 className="wizard-step-title">Alert already exists</h2>
                  </header>
                  <ErrorBanner
                    message={
                      createError || 'This alert already exists for this recipient and search.'
                    }
                  />
                </div>
              )}

              {phase === 'error' && (
                <div className="wizard-error-panel" role="alert">
                  <header className="wizard-step-header">
                    <h2 className="wizard-step-title">Create alert</h2>
                  </header>
                  <ErrorBanner message={createError || 'Setup failed. Please try again.'} />
                </div>
              )}
            </fieldset>

            <WizardFooter
              phase={phase}
              createdSearchId={createdSearchId}
              duplicateSubscriptionId={duplicateSubId}
              errorMessage={createError}
              onBack={goBack}
              onNext={goNext}
              onStartOver={() => resetWizard()}
              onCreateAnother={() => resetWizard()}
              onRetry={() => void createAlert()}
              onChooseRecipient={() => {
                setCreateError(null);
                setDuplicateSubId(null);
                setPhase('recipient');
                submitInFlight.current = false;
              }}
              onBackToSearch={() => {
                setCreateError(null);
                setDuplicateSubId(null);
                setPhase('search');
                submitInFlight.current = false;
              }}
            />
          </form>
        </div>
      )}
    </div>
  );
}
